import { createTheme } from '@mui/material';
import { FONT_FAMILY_STACK } from './typography';

// Augment the palette to include an porterGrey color
declare module '@mui/material/styles' {
  interface Palette {
    accent: Palette['primary'];
    darkGrey: Palette['primary'];
    medGrey: Palette['primary'];
    lightGrey: Palette['primary'];
    darkerLightGrey: Palette['primary'];
    lineGrey: Palette['primary'];
    outlineGrey: Palette['primary'];
    lightOutlineGrey: Palette['primary'];
    warning: Palette['primary'];
  }

  interface PaletteOptions {
    accent?: PaletteOptions['primary'];
    darkGrey?: PaletteOptions['primary'];
    medGrey?: PaletteOptions['primary'];
    lightGrey?: PaletteOptions['primary'];
    darkerLightGrey?: PaletteOptions['primary'];
    lineGrey?: PaletteOptions['primary'];
    outlineGrey?: PaletteOptions['primary'];
    lightOutlineGrey?: PaletteOptions['primary'];
    warning?: PaletteOptions['primary'];
  }

  interface TypeText {
    tertiary: string;
  }

  interface TypographyVariants {
    display: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    display?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    smallLink: true;
    link: true;
    light: true;
    base: true;
  }
}

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsVariantOverrides {
    smallLink: true;
    link: true;
    light: true;
    base: true;
  }
}

const fontFamily = FONT_FAMILY_STACK.join(',');

const rem = (px: number) => `${px / 16}rem`;

const theme = createTheme({
  typography: {
    fontFamily,
    display: {
      fontFamily,
      fontSize: rem(34),
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h1: { fontSize: rem(26), fontWeight: 600, lineHeight: 1.25 },
    h2: { fontSize: rem(22), fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: rem(18), fontWeight: 600, lineHeight: 1.35 },
    h4: { fontSize: rem(16), fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: rem(14), fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: rem(16), fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: rem(14), fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: rem(16), fontWeight: 400, lineHeight: 1.5 },
    body2: { fontSize: rem(14), fontWeight: 400, lineHeight: 1.5 },
    caption: { fontSize: rem(12), fontWeight: 400, lineHeight: 1.4 },
    overline: {
      fontSize: rem(11),
      fontWeight: 700,
      lineHeight: 1.4,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    button: {
      fontSize: rem(14),
      fontWeight: 600,
      lineHeight: 1.4,
      textTransform: 'none',
    },
  },
  palette: {
    primary: {
      main: '#FF3008',
      light: '#FFD6CE',
    },
    secondary: {
      main: '#00A86B',
      dark: '#006B47',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
      tertiary: '#767676',
      disabled: '#BDBDBD',
    },
    accent: {
      main: '#CEEEE3',
      contrastText: '#000000',
    },
    darkGrey: {
      main: '#333333',
      contrastText: '#FFFFFF',
    },
    medGrey: {
      main: '#666666',
      contrastText: '#FFFFFF',
    },
    outlineGrey: {
      main: '#939393',
      contrastText: '#FFFFFF',
    },
    lightGrey: {
      main: '#F5F5F5',
      contrastText: '#000000',
    },
    darkerLightGrey: {
      main: '#D7D7D7',
      contrastText: '#000000',
    },
    // Mirrors the --lineGrey css variable in App.css.
    lineGrey: {
      main: '#EDEEEF',
      contrastText: '#000000',
    },
    lightOutlineGrey: {
      main: '#bdbdbd',
      contrastText: '#000000',
    },
    warning: {
      light: '#FDF8C9',
      main: '#FBD992',
      dark: '#B13600',
      contrastText: '#B13600',
    },
    error: {
      main: '#EB5757',
      dark: '#C62828',
      contrastText: '#FFFFFF',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          display: 'span',
          // Visual styles by default; genuine headings opt in with component=.
          h4: 'p',
          h5: 'p',
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: {},
          style: {
            fontFamily,
          },
        },
        {
          props: { variant: 'base' },
          style: ({ theme: currentTheme }) => ({
            color: '#ffffff',
            backgroundColor: currentTheme.palette.primary.main,
            fontSize: '0.875rem',
            fontWeight: 500,
            padding: '0.75rem 1rem',
            borderRadius: '10px',
            border: 'none',
            ':disabled': {
              opacity: 0.8,
              color: currentTheme.palette.lightGrey.main,
            },
            ':hover': {
              backgroundColor: '#c30000',
            },
          }),
        },
        {
          props: { variant: 'base', color: 'secondary' },
          style: ({ theme: currentTheme }) => ({
            color: '#ffffff',
            backgroundColor: currentTheme.palette.secondary.main,
            fontSize: '0.875rem',
            fontWeight: 500,
            padding: '0.75rem 1rem',
            borderRadius: '10px',
            border: 'none',
            ':disabled': {
              opacity: 0.8,
              color: currentTheme.palette.lightGrey.main,
            },
            ':hover': {
              backgroundColor: '#008040',
            },
          }),
        },
        {
          props: { variant: 'smallLink' },
          style: ({ theme: currentTheme }) => ({
            color: currentTheme.palette.darkGrey.main,
            fontSize: currentTheme.typography.body2.fontSize,
            fontWeight: 400,
            textDecoration: 'underline',
            padding: 0,
            ':hover': {
              backgroundColor: 'transparent',
            },
          }),
        },
        {
          props: { variant: 'link' },
          style: {
            color: '#000000',
            fontSize: '0.875rem',
            fontWeight: 700,
            textDecoration: 'underline',
            padding: 0,
            ':hover': {
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'light' },
          style: ({ theme: currentTheme }) => ({
            color: currentTheme.palette.darkGrey.main,
            fontSize: '0.875rem',
            fontWeight: 500,
            padding: '0.75rem 1rem',
            borderRadius: '10px',
            border: `1px solid ${currentTheme.palette.lightOutlineGrey.main}`,
            ':disabled': {
              backgroundColor: currentTheme.palette.lightGrey.main,
              color: currentTheme.palette.medGrey.main,
              opacity: 0.8,
            },
          }),
        },
        {
          props: { variant: 'light', color: 'secondary' },
          style: ({ theme: currentTheme }) => ({
            border: 'none',
            backgroundColor: currentTheme.palette.accent.main,
            color: currentTheme.palette.secondary.main,
            ':hover': {
              backgroundColor: currentTheme.palette.accent.main,
              filter: 'brightness(0.95)',
            },
            ':disabled': {
              backgroundColor: currentTheme.palette.accent.main,
              color: currentTheme.palette.secondary.main,
              opacity: 0.8,
            },
          }),
        },
        {
          props: { variant: 'light', color: 'info' },
          style: ({ theme: currentTheme }) => ({
            border: 'none',
            backgroundColor: currentTheme.palette.lightGrey.main,
            color: currentTheme.palette.darkGrey.main,
            ':hover': {
              backgroundColor: currentTheme.palette.lightGrey.main,
              filter: 'brightness(0.95)',
            },
            ':disabled': {
              backgroundColor: currentTheme.palette.lightGrey.main,
              color: currentTheme.palette.darkGrey.main,
              opacity: 0.8,
            },
          }),
        },
      ],
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          paddingRight: '45px !important',
        },
      },
    },
    // remove border from right button in button group
    MuiButtonGroup: {
      styleOverrides: {
        firstButton: {
          borderRight: 'none !important',
        },
      },
    },
  },
});

export default theme;
